import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ease } from '@/lib/motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarX,
  CircleDashed,
  Clock,
  LayoutGrid,
  List,
  Megaphone,
  Plus,
} from 'lucide-react';
import type { Task, UserPublic } from '@underline/shared';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonList } from '@/components/ui/skeleton-card';
import { EventRow } from '@/components/events/event-card';
import { TaskRow } from '@/components/tasks/task-card';
import { UserAvatar } from '@/components/users/user-avatar';
import { useEvents, type EventWithTasks } from '@/hooks/use-events';
import { useTasks, useToggleTask } from '@/hooks/use-tasks';
import { useUsers } from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore, type DashLayout } from '@/stores/ui-store';
import { useCountUp } from '@/hooks/use-count-up';
import {
  fmtCouvertParts,
  fmtDateBR,
  fmtTime,
  fmtWeekdayLong,
  isOverdue,
  todayISO,
} from '@/lib/dates';
import { cn } from '@/lib/utils';

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const openNewEvent = useUiStore((s) => s.openNewEvent);
  const dashLayout = useUiStore((s) => s.dashLayout);
  const setDashLayout = useUiStore((s) => s.setDashLayout);
  const { data: events = [], isLoading: loadingEvents } = useEvents();
  const { data: tasks = [], isLoading: loadingTasks } = useTasks();
  const { data: users = [] } = useUsers();
  const toggleTask = useToggleTask();

  const today = todayISO();
  // Lista completa de eventos futuros (ordenada) — usada no layout "lista".
  const upcomingList = useMemo(
    () =>
      events
        .filter((e) => e.event_date >= today)
        .sort((a, b) => a.event_date.localeCompare(b.event_date)),
    [events, today],
  );
  // Próximo evento (primeiro da lista) — usado no hero do layout "cards".
  const nextEvent = upcomingList[0];

  const pending = tasks.filter((t) => t.status === 'pending');
  const overdue = pending.filter((t) => isOverdue(t.due_date, t.due_time));
  const nextPosts = [...pending]
    .sort((a, b) =>
      `${a.due_date} ${a.due_time ?? ''}`.localeCompare(
        `${b.due_date} ${b.due_time ?? ''}`,
      ),
    )
    .slice(0, 5);

  const pendingCount = useCountUp(pending.length);
  const overdueCount = useCountUp(overdue.length);

  const firstName = (user?.name ?? 'amigo').split(' ')[0];

  return (
    <div>
      {/* ── Header: saudação + subtítulo + toggle de layout ─────────── */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="u-h1 min-w-0">
            Olá, {firstName}
            <span className="u-cursor">_</span>
          </h1>
          <DashLayoutToggle value={dashLayout} onChange={setDashLayout} />
        </div>
        <p className="mt-2 text-sm leading-snug text-muted-foreground">
          {pending.length === 0
            ? 'Tudo em dia.'
            : `${pending.length} ${
                pending.length === 1 ? 'post pendente' : 'posts pendentes'
              }${
                overdue.length
                  ? ` · ${overdue.length} atrasado${overdue.length > 1 ? 's' : ''}`
                  : ''
              }.`}
        </p>
      </div>

      {dashLayout === 'cards' ? (
        <DashboardCards
          nextEvent={nextEvent}
          loadingEvents={loadingEvents}
          loadingTasks={loadingTasks}
          overdue={overdue}
          pendingCount={pendingCount}
          overdueCount={overdueCount}
          nextPosts={nextPosts}
          users={users}
          onNav={navigate}
          onCreate={openNewEvent}
        />
      ) : (
        <DashboardList
          upcoming={upcomingList.slice(0, 5)}
          loadingEvents={loadingEvents}
          loadingTasks={loadingTasks}
          nextPosts={nextPosts}
          users={users}
          onNav={navigate}
          onToggleTask={(id) => toggleTask.mutate(id)}
        />
      )}
    </div>
  );
}

/* ── DashLayoutToggle — mini segmented Cards / Lista ─────────────────── */
function DashLayoutToggle({
  value,
  onChange,
}: {
  value: DashLayout;
  onChange: (layout: DashLayout) => void;
}) {
  const options: { value: DashLayout; label: string; icon: typeof LayoutGrid }[] = [
    { value: 'cards', label: 'Cards', icon: LayoutGrid },
    { value: 'lista', label: 'Lista', icon: List },
  ];
  return (
    <div
      role="tablist"
      aria-label="Layout do painel"
      className="inline-flex items-center gap-0.5 rounded-full border border-border bg-surface-2 p-[3px]"
    >
      {options.map((o) => {
        const active = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-wider transition-colors',
              active
                ? 'bg-primary text-primary-foreground shadow-red'
                : 'text-muted-foreground',
            )}
          >
            <Icon className="h-3 w-3" strokeWidth={2} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Layout "cards": hero + KPIs + lista de posts ────────────────────── */
function DashboardCards({
  nextEvent,
  loadingEvents,
  loadingTasks,
  overdue,
  pendingCount,
  overdueCount,
  nextPosts,
  users,
  onNav,
  onCreate,
}: {
  nextEvent: EventWithTasks | undefined;
  loadingEvents: boolean;
  loadingTasks: boolean;
  overdue: Task[];
  pendingCount: number;
  overdueCount: number;
  nextPosts: Task[];
  users: UserPublic[];
  onNav: (path: string) => void;
  onCreate: () => void;
}) {
  return (
    <>
      {/* Hero do próximo evento */}
      <div className="mb-4">
        {loadingEvents ? (
          <div className="h-40 animate-pulse rounded-2xl border border-border bg-card p-5" />
        ) : nextEvent ? (
          <motion.button
            type="button"
            onClick={() => onNav('/calendar')}
            whileTap={{ scale: 0.99 }}
            className="relative w-full overflow-hidden rounded-2xl bg-foreground px-5 py-[22px] text-left text-background shadow-lift"
          >
            {/* Brilho radial vermelho no canto */}
            <div
              className="pointer-events-none absolute right-0 top-0 h-[110px] w-[110px] opacity-50"
              style={{
                background:
                  'radial-gradient(circle at 70% 30%, var(--primary) 0%, transparent 65%)',
              }}
            />
            <div className="relative">
              <p className="font-mono text-[10px] font-medium uppercase tracking-wider text-background/65">
                próximo evento
              </p>
              <h2 className="u-event-title mb-2 mt-2.5 text-[22px] text-background">
                {nextEvent.name}
              </h2>
              <p className="font-mono text-xs uppercase tracking-wide text-background/75">
                {fmtWeekdayLong(nextEvent.event_date)}
              </p>
              <div className="mt-[18px] grid grid-cols-3 gap-2">
                <HeroStat label="Abre" value={fmtTime(nextEvent.open_time) || '—'} />
                <HeroStat label="Fecha" value={fmtTime(nextEvent.close_time) || '—'} />
                {(() => {
                  const c = fmtCouvertParts(
                    nextEvent.couvert_type,
                    nextEvent.couvert_value,
                    nextEvent.couvert_until,
                  );
                  return <HeroStat label={c.label} value={c.value} />;
                })()}
              </div>
            </div>
          </motion.button>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card">
            <EmptyState
              icon={<CalendarX className="h-5 w-5" />}
              title="Nenhum evento agendado"
              description="Que tal começar planejando a próxima noite?"
              action={
                <button
                  type="button"
                  onClick={onCreate}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97]"
                >
                  <Plus className="h-[15px] w-[15px]" />
                  Criar evento
                </button>
              }
            />
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="mb-7 grid grid-cols-2 gap-2.5">
        <Kpi
          label="Pendentes"
          value={pendingCount}
          icon={<CircleDashed className="h-[15px] w-[15px]" />}
          delay={0}
          onClick={() => onNav('/marketing')}
        />
        <Kpi
          label="Atrasados"
          value={overdueCount}
          icon={<AlertTriangle className="h-[15px] w-[15px]" />}
          accent={overdue.length > 0}
          delay={0.07}
          onClick={() => onNav('/marketing')}
        />
      </div>

      {/* Próximos posts */}
      <div>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="u-h3">Próximos posts</h2>
          <button
            type="button"
            onClick={() => onNav('/marketing')}
            className="text-xs font-semibold text-primary"
          >
            Ver todos →
          </button>
        </div>

        {loadingTasks ? (
          <SkeletonList count={3} />
        ) : nextPosts.length === 0 ? (
          <div className="rounded-xl bg-surface-2 px-5 py-8 text-center text-sm text-muted-foreground">
            Tudo em dia.
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {nextPosts.map((t) => {
              const u = users.find((x) => x.id === t.responsible_id);
              const od = isOverdue(t.due_date, t.due_time);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onNav('/marketing')}
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left"
                  >
                    <span
                      className={cn(
                        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                        od ? 'bg-primary-soft text-primary' : 'bg-surface-2 text-muted-foreground',
                      )}
                    >
                      <Megaphone className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {t.title}
                      </span>
                      <span
                        className={cn(
                          'mt-[3px] inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-wide',
                          od ? 'text-destructive' : 'text-muted-foreground',
                        )}
                      >
                        <Clock className="h-[11px] w-[11px]" />
                        {fmtDateBR(t.due_date)}
                        {t.due_time && ` · ${fmtTime(t.due_time)}`}
                      </span>
                    </span>
                    {u && (
                      <UserAvatar
                        name={u.name}
                        colorHex={u.color_hex}
                        url={u.avatar_url}
                        size="sm"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ── HeroStat — métrica compacta dentro do hero (fundo escuro) ───────── */
function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-wider text-background/60">{label}</p>
      <p className="mt-[3px] truncate font-mono text-[13px] font-semibold tracking-tight text-background">
        {value}
      </p>
    </div>
  );
}

/* ── Kpi — card numérico clicável ────────────────────────────────────── */
function Kpi({
  label,
  value,
  icon,
  accent,
  delay = 0,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent?: boolean;
  delay?: number;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease, delay }}
      whileHover={{ y: -2, boxShadow: 'var(--shadow-card)' }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'flex flex-col gap-3.5 rounded-2xl border bg-card px-4 pb-3.5 pt-4 text-left shadow-elev',
        accent ? 'border-destructive/35' : 'border-border',
      )}
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <span className={cn('inline-flex', accent ? 'text-destructive' : 'text-muted-foreground')}>
          {icon}
        </span>
      </div>
      <p
        className={cn(
          'font-display text-4xl font-bold leading-none tracking-tight tabular-nums',
          accent ? 'text-destructive' : 'text-foreground',
        )}
      >
        {value}
      </p>
    </motion.button>
  );
}

/* ── Layout "lista": próximos eventos + próximos posts em cards ──────── */
function DashboardList({
  upcoming,
  loadingEvents,
  loadingTasks,
  nextPosts,
  users,
  onNav,
  onToggleTask,
}: {
  upcoming: EventWithTasks[];
  loadingEvents: boolean;
  loadingTasks: boolean;
  nextPosts: Task[];
  users: UserPublic[];
  onNav: (path: string) => void;
  onToggleTask: (id: number) => void;
}) {
  return (
    <>
      {/* Próximos eventos */}
      <div>
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className="u-h3">Próximos eventos</h2>
          <button
            type="button"
            onClick={() => onNav('/calendar')}
            className="text-xs font-semibold text-primary"
          >
            Ver todos →
          </button>
        </div>
        <div className="mb-6 overflow-hidden rounded-2xl border border-border bg-card">
          {loadingEvents ? (
            <SkeletonList count={3} />
          ) : upcoming.length === 0 ? (
            <div className="px-4 py-7 text-center text-sm text-muted-foreground">
              Nada agendado.
            </div>
          ) : (
            upcoming.map((e, idx) => (
              <div
                key={e.id}
                className={cn(idx === upcoming.length - 1 && '[&_button]:border-b-0')}
              >
                <EventRow event={e} onClick={() => onNav('/calendar')} />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Próximos posts */}
      <div>
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className="u-h3">Próximos posts</h2>
          <button
            type="button"
            onClick={() => onNav('/marketing')}
            className="text-xs font-semibold text-primary"
          >
            Ver todos →
          </button>
        </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {loadingTasks ? (
            <SkeletonList count={3} />
          ) : nextPosts.length === 0 ? (
            <div className="px-4 py-7 text-center text-sm text-muted-foreground">
              Tudo em dia.
            </div>
          ) : (
            nextPosts.map((t) => {
              const u = users.find((x) => x.id === t.responsible_id);
              return (
                <TaskRow
                  key={t.id}
                  task={t}
                  responsible={u}
                  onToggle={() => onToggleTask(t.id)}
                  onClick={() => onNav('/marketing')}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
